import React, { useState, useEffect, useRef, useMemo, Component, ErrorInfo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Citation } from '../../store/actions/ChatAction';
import { docRefPlugin } from './utils/docRefPlugin';
import rehypeRaw from 'rehype-raw';
import ResearchResultRenderer, { tryParseSynthesis } from './ResearchResultRenderer';

/* ── Error boundary to prevent synthesis renderer crashes from killing the page ── */
class SynthesisErrorBoundary extends Component<
  { children: React.ReactNode; fallbackText: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[SynthesisErrorBoundary] Render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="chat-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {this.props.fallbackText}
          </ReactMarkdown>
        </div>
      );
    }
    return this.props.children;
  }
}

// Styles — aligned with platform fonts: Poppins (body), Mulish (headings)
const Styles = `
  @keyframes paragraphReveal {
    0% { opacity: 0; transform: translateY(12px); filter: blur(1px); }
    60% { filter: blur(0); }
    100% { opacity: 1; transform: translateY(0); filter: blur(0); }
  }
  .chat-paragraph-enter {
    animation: paragraphReveal 0.45s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  }

  .chat-content {
    font-family: 'Poppins', sans-serif; 
    font-size: 0.875rem; 
    line-height: 1.6; 
    color: inherit;
    margin: 0; 
    padding: 0;
  }
  .chat-content p { 
    margin: 0.625rem 0; 
  }
  .chat-content h1, .chat-content h2, .chat-content h3, .chat-content h4, .chat-content h5, .chat-content h6 {
    font-family: 'Mulish', sans-serif; 
    margin: 0.875rem 0 0.375rem; 
    font-weight: 600;
  }
  .chat-content h1 { font-size: 1.2rem; }
  .chat-content h2 { font-size: 1.1rem; }
  .chat-content h3 { font-size: 1.05rem; }
  .chat-content h4 { font-size: 0.95rem; }
  .chat-content h5 { font-size: 0.875rem; }
  .chat-content h6 { font-size: 0.8125rem; }
  .chat-content ul, .chat-content ol {
    margin: 0.625rem 0; 
    padding-left: 1.25rem; 
    font-family: 'Poppins', sans-serif;
  }
  .chat-content ul { list-style-type: disc; }
  .chat-content ol { list-style-type: decimal; }
  .chat-content li { 
    margin: 0.25rem 0; 
  }
  .chat-content code {
    font-family: 'Montserrat', sans-serif; 
    background: #f4f4f4; 
    padding: 0.2rem 0.4rem;
    border-radius: 4px; 
    font-size: 0.8125rem; 
    color: #d63384;
  }
  .chat-content pre {
    background: #f6f8fa; 
    padding: 0.75rem; 
    border-radius: 8px; 
    overflow-x: auto;
    font-family: 'Montserrat', sans-serif; 
    font-size: 0.8125rem; 
    color: #e83e8c;
    margin: 0.75rem 0;
  }
  .chat-content blockquote {
    margin: 0.75rem 0; 
    padding: 0.5rem 1rem; 
    border-left: 4px solid #d1d5db;
    background: #f9fafb; 
    font-style: italic;
  }
  .chat-content sup {
    font-family: 'Poppins', sans-serif; 
    font-size: 0.7rem; 
    vertical-align: super;
    margin-left: 2px;
  }
  .chat-content strong { font-weight: 600; }
  .chat-content hr { 
    border: 0; 
    border-top: 1px solid #d1d5db; 
    margin: 1rem 0; 
  }
  .chat-content table { 
    border-collapse: collapse; 
    margin: 1rem 0; 
    width: 100%; 
  }
  .chat-content th, .chat-content td { 
    border: 1px solid #d1d5db; 
    padding: 0.5rem; 
    text-align: left; 
  }
  .chat-content th { 
    background: #f9fafb; 
    font-weight: 700; 
  }
  .chat-content img { 
    max-width: 100%; 
    height: auto; 
    margin: 1rem 0; 
  }
.chat-content sup a{
  color:#2563eb;            
  font-weight:600;          
  text-decoration:none;
  font-size:.7em;
  vertical-align:super;
  transition:text-decoration .15s;
}
.chat-content sup a:hover{ text-decoration:underline; }
.chat-content a{
  color:#2563eb;            
  font-weight:600;          
  text-decoration:none;
  font-size:.7em;
  vertical-align:super;
  transition:text-decoration .15s;
}
.chat-content a:hover{ text-decoration:underline; }

`;

type DocLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    ['data-doc']?: string;
  };

interface RenderContentProps {
  text: string;
  citations: Citation[];
  isLive: boolean;
  onComplete: () => void;
  onOpenCitation: (url: string) => void;
  synthesisData?: Record<string, any>;
}

export const RenderContent: React.FC<RenderContentProps> = ({ text: rawText, citations, isLive, onComplete, onOpenCitation, synthesisData }) => {
  // Ensure text is always a string to prevent runtime errors on blob-loaded objects
  const text = typeof rawText === 'string' ? rawText : (rawText ? String(rawText) : '');
  const [revealedCount, setRevealedCount] = useState(0);
  const cancelRef = useRef<() => void>(() => {});
  const PARAGRAPH_DELAY = 120; // ms between paragraph reveals

  // Split text into paragraph-sized chunks (double newline boundaries).
  // Preserves markdown structure within each paragraph.
  const paragraphs = useMemo(() => {
    if (!text) return [];
    // Split on double+ newlines, keeping the delimiters so markdown rendering is correct
    const parts = text.split(/(\n{2,})/);
    // Re-join into logical paragraphs (content + its trailing whitespace)
    const result: string[] = [];
    let current = '';
    for (const part of parts) {
      if (/^\n{2,}$/.test(part)) {
        if (current.trim()) result.push(current);
        current = '';
      } else {
        current += part;
      }
    }
    if (current.trim()) result.push(current);
    return result.length > 0 ? result : [text];
  }, [text]);

  // Detect structured synthesis JSON — prefer explicit prop, fall back to parsing text
  const parsedSynthesis = useMemo(() => {
    if (synthesisData && (synthesisData.executive_summary || synthesisData.detailed_analysis)) {
      return synthesisData;
    }
    return tryParseSynthesis(text);
  }, [text, synthesisData]);          

  // Progressive paragraph reveal for live streaming responses
  useEffect(() => {
    if (!isLive || text.trim() === '' || parsedSynthesis) {
      setRevealedCount(paragraphs.length);
      onComplete();
      return;
    }

    let cancelled = false;
    let idx = 0;
    cancelRef.current = () => { cancelled = true; };

    const tick = () => {
      if (cancelled) return;
      idx++;
      setRevealedCount(idx);
      if (idx < paragraphs.length) {
        setTimeout(tick, PARAGRAPH_DELAY);
      } else {
        onComplete();
      }
    };
    tick();

    return () => cancelRef.current();
  }, [text, isLive, parsedSynthesis]);

  // When rendering structured synthesis, signal completion immediately
  useEffect(() => {
    if (parsedSynthesis) {
      onComplete();
    }
  }, [parsedSynthesis]);

  const citationMap = citations.reduce((acc, c) => {
    acc[c.number as number] = { url: c.url };
    return acc;
  }, {} as Record<number, { url: string }>);

  // ── Structured synthesis → rich research renderer ──
  if (parsedSynthesis) {
    return (
      <>
        <style>{Styles}</style>
        <SynthesisErrorBoundary fallbackText={text}>
          <ResearchResultRenderer data={parsedSynthesis} onOpenCitation={onOpenCitation} />
        </SynthesisErrorBoundary>
      </>
    );
  }

  // ── Paragraph-by-paragraph reveal with smooth fade-in-down ──
  const visibleParagraphs = paragraphs.slice(0, revealedCount);
  const isFullyRevealed = revealedCount >= paragraphs.length;

  return (
    <>
      <style>{Styles}</style>
      <div className="chat-content">
        {visibleParagraphs.map((para, i) => {
          // The latest paragraph(s) get the enter animation; earlier ones are static
          const isNew = isLive && !isFullyRevealed && i === visibleParagraphs.length - 1;
          return (
            <div key={i} className={isNew ? 'chat-paragraph-enter' : ''}>
              <ReactMarkdown
                remarkPlugins={[
                  remarkGfm,
                  [docRefPlugin, { map: citationMap }],
                ]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ children, href, ...rest }) => {
                    const { ['data-doc']: dataDoc, ...anchorRest } = rest as DocLinkProps;
                    const num = Number(dataDoc ?? '');

                    return (
                      <sup>
                        <a
                          href={href}
                          data-doc={dataDoc}
                          {...anchorRest}
                          onClick={(e) => {
                            e.preventDefault();
                            const c = citations.find((c) => c.number === num);
                            if (c) onOpenCitation(c.url || `#citation-${num}`);
                          }}
                          className="text-blue-500"
                        >
                          {children}
                        </a>
                      </sup>
                    );
                  },
                }}
              >
                {para}
              </ReactMarkdown>
            </div>
          );
        })}
      </div>
    </>
  );
};